import React from 'react';
import { useTranslation } from 'react-i18next';

export type SearchFieldScopeProps = {
  fields: readonly string[];
  selected: string[];
  onToggle: (field: string) => void;
  className?: string;
  /** Optional aria label for the fieldset */
  legend?: string;
};

/**
 * Compact checkbox row for choosing which fields participate in text search.
 */
export const SearchFieldScope: React.FC<SearchFieldScopeProps> = ({
  fields,
  selected,
  onToggle,
  className = '',
  legend,
}) => {
  const { t } = useTranslation();
  const title = legend || t('searchFields.legend') || 'Search in';

  return (
    <fieldset className={`min-w-0 ${className}`}>
      <legend className="sr-only">{title}</legend>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-xs font-medium text-gray-500 shrink-0">{title}:</span>
        {fields.map((field) => {
          const id = `search-field-${field}`;
          const checked = selected.includes(field);
          const label =
            t(`searchFields.${field}`, { defaultValue: field }) || field;
          return (
            <label
              key={field}
              htmlFor={id}
              className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none"
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(field)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default SearchFieldScope;
